import {CountriesFieldWithAllowUsOnlyToggleType} from "../CountriesFieldWithAllowUsOnlyToggle/CountriesFieldWithAllowUsOnlyToggleUtils"
import {IdentificationTypes, OrgBankAgreementBankRiskSettings} from "../../resources/bankAgreementDefaults"

export type IdentificationGroup = {
	ssn: boolean
	itin: boolean
	passport: boolean
	matriculaConsular: boolean
}

export type AcceptableTypesOfIdentificationType = {
	individual: IdentificationGroup
	soleProprietor: IdentificationGroup
	businessBeneficialOwnerOrOfficer: IdentificationGroup
}

type CheckboxGroupKey = keyof AcceptableTypesOfIdentificationType // 'customerBeneficialOwnerOrOfficer' | 'individual'
export type CheckboxItemKey = keyof AcceptableTypesOfIdentificationType[CheckboxGroupKey] // 'ssn' | 'itin' | 'passport' | 'matriculaConsular'

interface CheckboxItem {
	name: CheckboxItemKey
	label: string
}

interface CheckboxGroup {
	group: CheckboxGroupKey
	title: string
	items: CheckboxItem[]
}

export type CheckboxStructure = CheckboxGroup[]

export const checkboxStructure: CheckboxStructure = [
	{
		group: "individual",
		title: "Individual",
		items: [
			{name: "ssn", label: "Social Security Number"},
			{name: "itin", label: "Individual Taxpayer ID Number (ITIN)"},
			{name: "passport", label: "Passport"},
			{name: "matriculaConsular", label: "Matricula Consular"},
		],
	},
	{
		group: "soleProprietor",
		title: "Sole Proprietor",
		items: [
			{name: "ssn", label: "Social Security Number"},
			{name: "itin", label: "Individual Taxpayer ID Number (ITIN)"},
			{name: "passport", label: "Passport"},
			{name: "matriculaConsular", label: "Matricula Consular"},
		],
	},
	{
		group: "businessBeneficialOwnerOrOfficer",
		title: "Business - Owner/Officer",
		items: [
			{name: "ssn", label: "Social Security Number"},
			{name: "itin", label: "Individual Taxpayer ID Number (ITIN)"},
			{name: "passport", label: "Passport"},
			{name: "matriculaConsular", label: "Matricula Consular"},
		],
	},
]

export type CountriesOfOperationType = {isUsPresenceRequired: boolean} & CountriesFieldWithAllowUsOnlyToggleType

export type ProhibitedCountriesType = {
	individualCustomer: {
		physicalAddress: CountriesFieldWithAllowUsOnlyToggleType
		citizenship: string[]
	}
	soleProprietorCustomer: {
		physicalAddress: CountriesFieldWithAllowUsOnlyToggleType
		citizenship: string[]
	}
	businessCustomer: {
		physicalAddress: CountriesFieldWithAllowUsOnlyToggleType
		countriesOfOperation: CountriesOfOperationType
		beneficialOwnerOrOfficerAddress: CountriesFieldWithAllowUsOnlyToggleType
		beneficialOwnerOrOfficerCitizenship: string[]
	}
}

export enum RiskSettingsFieldNames {
	AcceptableTypesOfIdentification = "acceptableTypesOfIdentification",
	ProhibitedBusinessVerticals = "prohibitedBusinessVerticals",
	ProhibitedCountries = "prohibitedCountries",
}

export type RiskSettingsType = {
	[RiskSettingsFieldNames.AcceptableTypesOfIdentification]: AcceptableTypesOfIdentificationType
	[RiskSettingsFieldNames.ProhibitedBusinessVerticals]: string[]
	[RiskSettingsFieldNames.ProhibitedCountries]: ProhibitedCountriesType
}

export const hasExceptionsOnAcceptableTypesOfIdentification = (
	groupValues: IdentificationGroup,
	groupDefaultValues: IdentificationGroup
): boolean =>
	Object.keys(groupDefaultValues).some((key) => {
		const checkboxKey = key as CheckboxItemKey
		return groupValues[checkboxKey] !== groupDefaultValues[checkboxKey]
	})

export const convertToRiskSettings = (
	orgBankAgreementRiskSettings: OrgBankAgreementBankRiskSettings
): RiskSettingsType => {
	const mapIdentification = (types: string[]): IdentificationGroup => ({
		ssn: types.includes("SSN"),
		itin: types.includes("ITIN"),
		passport: types.includes("Passport"),
		matriculaConsular: types.includes("MatriculaConsular"),
	})

	return {
		[RiskSettingsFieldNames.AcceptableTypesOfIdentification]: {
			individual: mapIdentification(orgBankAgreementRiskSettings.allowedIndividualIdentificationTypes ?? []),
			soleProprietor: mapIdentification(orgBankAgreementRiskSettings.allowedSolePropsIdentificationTypes ?? []),
			businessBeneficialOwnerOrOfficer: mapIdentification(
				orgBankAgreementRiskSettings.allowedBusinessIdentificationTypes ?? []
			),
		},
		[RiskSettingsFieldNames.ProhibitedBusinessVerticals]:
			orgBankAgreementRiskSettings.prohibitedBusinessVerticals ?? [],
		[RiskSettingsFieldNames.ProhibitedCountries]: {
			individualCustomer: {
				physicalAddress: orgBankAgreementRiskSettings.individualPhysicalAddressAllowUSOnly
					? {isAllowUsOnly: true, countries: null}
					: {isAllowUsOnly: false, countries: orgBankAgreementRiskSettings.prohibitedIndividualPhysicalAddress ?? []},
				citizenship: orgBankAgreementRiskSettings.prohibitedIndividualCitizenship ?? [],
			},
			soleProprietorCustomer: {
				physicalAddress: orgBankAgreementRiskSettings.solePropsPhysicalAddressAllowUSOnly
					? {isAllowUsOnly: true, countries: null}
					: {isAllowUsOnly: false, countries: orgBankAgreementRiskSettings.prohibitedSolePropsPhysicalAddress ?? []},
				citizenship: orgBankAgreementRiskSettings.prohibitedSolePropsCitizenship ?? [],
			},
			businessCustomer: {
				physicalAddress: orgBankAgreementRiskSettings.businessPhysicalAddressAllowUSOnly
					? {isAllowUsOnly: true, countries: null}
					: {isAllowUsOnly: false, countries: orgBankAgreementRiskSettings.prohibitedBusinessPhysicalAddress ?? []},
				countriesOfOperation: orgBankAgreementRiskSettings.countriesOfOperationAllowUSOnly
					? {
							isUsPresenceRequired: orgBankAgreementRiskSettings.isUSPresenceRequired ?? false,
							isAllowUsOnly: true,
							countries: null,
					  }
					: {
							isUsPresenceRequired: orgBankAgreementRiskSettings.isUSPresenceRequired ?? false,
							isAllowUsOnly: false,
							countries: orgBankAgreementRiskSettings.prohibitedCountriesOfOperation ?? [],
					  },
				beneficialOwnerOrOfficerAddress: orgBankAgreementRiskSettings.beneficialOwnerOrOfficerAddressAllowUSOnly
					? {isAllowUsOnly: true, countries: null}
					: {
							isAllowUsOnly: false,
							countries: orgBankAgreementRiskSettings.prohibitedBeneficialOwnerOrOfficerAddress ?? [],
					  },
				beneficialOwnerOrOfficerCitizenship:
					orgBankAgreementRiskSettings.prohibitedBeneficialOwnerOrOfficerCitizenship ?? [],
			},
		},
	}
}

export const convertToBankAgreementDefaultsRiskSettings = (riskSettingsType: RiskSettingsType): IdentificationTypes => {
	const extractIdentification = (group: IdentificationGroup): string[] => {
		return [
			group.ssn ? "SSN" : null,
			group.itin ? "ITIN" : null,
			group.passport ? "Passport" : null,
			group.matriculaConsular ? "MatriculaConsular" : null,
		].filter(Boolean) as string[]
	}
	const acceptableTypesOfIdentification = riskSettingsType[RiskSettingsFieldNames.AcceptableTypesOfIdentification]

	return {
		allowedIndividualIdentificationTypes: extractIdentification(acceptableTypesOfIdentification.individual),
		allowedSolePropsIdentificationTypes: extractIdentification(acceptableTypesOfIdentification.soleProprietor),
		allowedBusinessIdentificationTypes: extractIdentification(
			acceptableTypesOfIdentification.businessBeneficialOwnerOrOfficer
		),
	}
}

export const showProhibitedBusinessVerticalsAndCountries = false // TODO: remove it (as a true value) when the feature is implemented in the backend
